/**
 * Thema „Projekt-Befunde" (PV-037): was ein Befund ist, die drei Stufen, die Hochstufungsregel und
 * je AKTIVER Prüfregel Titel, Beschreibung, Zuständigkeit und Aktionen — aus dem Regelkatalog
 * (GET /v1/hilfe/befund-regeln). Nur bei Flag `befunde` sichtbar (Registry).
 */
import type { BefundAktion, BefundStufe } from '@projektverwaltung/shared';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '../../../lib/api.js';
import { STUFE_FARBE, useBefundLocale } from '../befunde/befund-basis.js';
import { HilfeHinweis } from './hilfe-hinweis.js';
import type { HilfeThemaProps } from './hilfe-typen.js';

const STUFEN: readonly BefundStufe[] = ['info', 'handeln', 'kritisch'];

/** Dieselben Aktionsnamen wie an den Befund-Zeilen (`v2.befund.aktion_*`). */
const AKTION_SCHLUESSEL: Record<BefundAktion, string> = {
  briefing_erzeugen: 'v2.befund.aktion_briefing',
  neu_pruefen: 'v2.befund.aktion_neu_pruefen',
  zurueckstellen: 'v2.befund.aktion_zurueckstellen',
  nachhaken: 'v2.befund.aktion_nachhaken',
  antworten: 'v2.befund.aktion_antworten',
};

export function ThemaBefunde({ kompakt }: HilfeThemaProps) {
  const { t } = useTranslation();
  const locale = useBefundLocale();
  const query = useQuery({
    queryKey: ['hilfe', 'befund-regeln', locale],
    queryFn: () => api.getHilfeBefundRegeln(locale),
    staleTime: 5 * 60_000,
  });
  if (query.isError) return <HilfeHinweis fehler>{t('hilfe.seite.fehler')}</HilfeHinweis>;
  const d = query.data;
  if (!d) return <HilfeHinweis>{t('hilfe.seite.laedt')}</HilfeHinweis>;

  const aktionenText = (aktionen: readonly BefundAktion[]): string =>
    t('hilfe.befunde.aktionen_label', {
      aktionen:
        aktionen.length === 0
          ? t('hilfe.befunde.keine_aktionen')
          : aktionen.map((a) => t(AKTION_SCHLUESSEL[a])).join(', '),
    });

  return (
    <div
      className={`space-y-3 ${kompakt ? 'text-xs' : 'text-sm'}`}
      style={{ color: 'var(--ink-2)' }}
    >
      <p>{t('hilfe.befunde.einleitung')}</p>
      <div className="space-y-1">
        <h3 className="font-medium" style={{ color: 'var(--ink)' }}>
          {t('hilfe.befunde.stufen_titel')}
        </h3>
        <ul className="space-y-1">
          {STUFEN.map((s) => (
            <li key={s} className="flex items-baseline gap-2">
              <span
                aria-hidden="true"
                className="inline-block h-2.5 w-2.5 shrink-0 rounded-full"
                style={{ background: STUFE_FARBE[s] }}
              />
              <span className="font-medium" style={{ color: 'var(--ink)' }}>
                {t(`v2.befund.stufe_${s}`)}
              </span>
              <span>{t(`hilfe.befunde.stufe.${s}`)}</span>
            </li>
          ))}
        </ul>
        <p>{t('hilfe.befunde.hochstufung', { tage: d.hochstufung_nach_tagen })}</p>
      </div>
      <div className="space-y-1">
        <h3 className="font-medium" style={{ color: 'var(--ink)' }}>
          {t('hilfe.befunde.regeln_titel')}
        </h3>
        {d.items.length === 0 ? (
          <p>{t('hilfe.befunde.keine_regeln')}</p>
        ) : (
          <ul className="space-y-2">
            {d.items.map((r) => (
              <li key={r.regel} className="space-y-0.5">
                <p className="font-medium" style={{ color: 'var(--ink)' }}>
                  {r.titel}
                </p>
                <p>{r.beschreibung}</p>
                <p className="text-xs" style={{ color: 'var(--ink-3)' }}>
                  {t('hilfe.befunde.zustaendig_label', {
                    rolle: t(`hilfe.befunde.zustaendig.${r.zustaendig}`),
                  })}
                </p>
                <p className="text-xs" style={{ color: 'var(--ink-3)' }}>
                  {aktionenText(r.aktionen)}
                </p>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
